import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ShieldCheck, CheckCircle2, Loader2, Heart } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { TiplymeLogo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const EMOJIS = ["🙏", "😊", "🔥", "💚", "⭐"];

export default function PayerPage() {
  const { code: routeCode, txn: txnParam } = useParams();
  const params = new URLSearchParams(window.location.search);
  const storedCode = typeof window !== "undefined" ? sessionStorage.getItem("tiplyme_code") : null;
  const storedTxn = typeof window !== "undefined" ? sessionStorage.getItem("tiplyme_txn") : null;
  const returnTxn = txnParam || params.get("txn") || (!routeCode ? storedTxn : null);
  const code = routeCode || storedCode;
  const isReturnMode = !routeCode || !!txnParam || !!params.get("txn");

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [custom, setCustom] = useState("");
  const [method] = useState("card");
  const [payingAmt, setPayingAmt] = useState(null);
  const [payingValue, setPayingValue] = useState(null);
  const [pendingTxn, setPendingTxn] = useState(null);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [rating, setRating] = useState(0);
  const [emoji, setEmoji] = useState("");
  const [fbDone, setFbDone] = useState(false);

  // Load the worker/card only for the tip form (not the return/confirmation screen).
  useEffect(() => {
    if (isReturnMode) return;
    if (!code) { setError("This payment link is invalid."); return; }
    api.get(`/pay/${code}`).then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
  }, [code, isReturnMode]);

  // Handle return from the hosted checkout: confirm status via our webhook/poll-updated record.
  useEffect(() => {
    const r = params.get("r");
    if (!isReturnMode) {
      if (r === "cancel" || r === "failure") setError(r === "cancel" ? "Payment cancelled. You can try again." : "Payment failed. Please try again.");
      return;
    }
    if (!returnTxn) {
      setError("We couldn't automatically confirm this payment. If it went through, the worker has already been credited — no need to pay again.");
      return;
    }
    setConfirming(true);
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const { data: st } = await api.get(`/pay/txn/${returnTxn}/status`);
        if (st.status === "success") {
          clearInterval(iv); setConfirming(false);
          sessionStorage.removeItem("tiplyme_txn"); sessionStorage.removeItem("tiplyme_code");
          setResult({ amount: st.amount, worker_name: st.worker_name, transaction_id: returnTxn });
          return;
        }
        if (st.status === "failed") {
          clearInterval(iv); setConfirming(false);
          sessionStorage.removeItem("tiplyme_txn"); sessionStorage.removeItem("tiplyme_code");
          setError("Your payment was not completed. Please try again.");
          return;
        }
      } catch { /* keep polling */ }
      if (tries > 25) {
        clearInterval(iv); setConfirming(false);
        setError("We're still confirming your payment — this can take a moment. Please check with the worker if unsure.");
      }
    }, 2000);
    return () => clearInterval(iv);
  }, [returnTxn]);

  const customAmount = custom ? parseFloat(custom) : null;
  const paying = payingAmt !== null;

  // While waiting on an external checkout (PWA opens it in a separate tab), poll our record so this
  // screen flips to success/rating the moment the payment confirms.
  useEffect(() => {
    if (!pendingTxn) return;
    const iv = setInterval(async () => {
      try {
        const { data: st } = await api.get(`/pay/txn/${pendingTxn}/status`);
        if (st.status === "success") {
          clearInterval(iv);
          sessionStorage.removeItem("tiplyme_txn"); sessionStorage.removeItem("tiplyme_code");
          setResult({ amount: st.amount, worker_name: st.worker_name, transaction_id: pendingTxn });
          setPendingTxn(null); setPayingAmt(null);
        } else if (st.status === "failed") {
          clearInterval(iv);
          setError("Your payment was not completed. Please try again.");
          setPendingTxn(null); setPayingAmt(null); setRedirectUrl(null);
        }
      } catch { /* keep polling */ }
    }, 2500);
    return () => clearInterval(iv);
  }, [pendingTxn]);

  const pay = async (value, key) => {
    if (!value || value <= 0 || paying) return;
    setError(""); setPayingAmt(key); setPayingValue(value);
    try {
      const { data: intent } = await api.post(`/pay/${code}/intent`, { amount: value, method });
      if (intent.redirect_url) {
        sessionStorage.setItem("tiplyme_txn", intent.transaction_id);
        sessionStorage.setItem("tiplyme_code", code);
        // Keep this screen live-polling in case the checkout opens in a separate tab (PWA/standalone),
        // so it auto-advances to the success/rating screen once payment confirms.
        setPayingValue(value); setPendingTxn(intent.transaction_id); setRedirectUrl(intent.redirect_url);
        window.location.replace(intent.redirect_url);
        return;
      }
      // Simulated mode — no keys / demo
      setResult({ amount: value, worker_name: data.worker.name, transaction_id: intent.transaction_id });
      setPayingAmt(null); setPayingValue(null);
    } catch (e) {
      setError(apiError(e));
      setPayingAmt(null); setPayingValue(null);
    }
  };

  const sendFeedback = async () => {
    try {
      await api.post(`/pay/${code}/feedback`, { transaction_id: result.transaction_id, rating: rating || null, emoji: emoji || null });
      setFbDone(true);
    } catch {
      setFbDone(true);
    }
  };

  const Shell = ({ children }) => (
    <div className="relative min-h-screen bg-slate-50">
      <div className="grain absolute inset-x-0 top-0 h-52 bg-brand-green" />
      <div className="relative mx-auto flex min-h-screen max-w-md flex-col px-5 pb-10 pt-6">
        <div className="flex items-center justify-between">
          <TiplymeLogo dark />
          <span className="flex items-center gap-1 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold text-white"><ShieldCheck className="h-3.5 w-3.5" /> Secure</span>
        </div>
        {children}
        <p className="mt-auto pt-8 text-center text-xs text-muted-foreground">TiplyMe · tiplyme.co.za</p>
      </div>
    </div>
  );

  const ResultCard = () => (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mt-8 rounded-3xl border border-border/60 bg-white p-6 text-center shadow-2xl" data-testid="payer-success">
      <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-brand-cyan/10 text-brand-cyan"><CheckCircle2 className="h-9 w-9" /></div>
      <h1 className="mt-4 font-heading text-2xl font-bold">Tip sent!</h1>
      <p className="mt-4 font-heading text-6xl font-extrabold leading-none text-brand-cyan" data-testid="payer-success-amount">R{result.amount}</p>
      <p className="mt-3 text-base text-muted-foreground">to <span className="font-semibold text-foreground">{result.worker_name}</span></p>
      {!fbDone ? (
        <div className="mt-6">
          <p className="overline-label text-muted-foreground">Rate the service</p>
          <div className="mt-2 flex justify-center gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <button key={s} onClick={() => setRating(s)} data-testid={`rating-star-${s}`}>
                <Star className={`h-8 w-8 transition-transform hover:scale-110 ${s <= rating ? "fill-brand-orange text-brand-orange" : "text-slate-300"}`} />
              </button>
            ))}
          </div>
          <div className="mt-4 flex justify-center gap-2">
            {EMOJIS.map((e) => (
              <button key={e} onClick={() => setEmoji(e)} data-testid={`emoji-${e}`}
                className={`grid h-11 w-11 place-items-center rounded-full border text-xl transition-transform hover:scale-110 ${emoji === e ? "border-brand-green bg-brand-green/10" : "border-border"}`}>{e}</button>
            ))}
          </div>
          <Button onClick={sendFeedback} data-testid="submit-feedback-button" className="mt-6 w-full rounded-full bg-brand-green font-semibold text-white hover:bg-brand-green/90">
            <Heart className="mr-1 h-4 w-4" /> Send feedback
          </Button>
        </div>
      ) : (
        <p className="mt-6 rounded-xl bg-brand-green/5 p-4 text-sm font-semibold text-brand-green" data-testid="feedback-thanks">Thank you! Your feedback helps {result.worker_name} get more work. 💚</p>
      )}
    </motion.div>
  );

  // ---- Return / confirmation screen (no card needed) ----
  if (isReturnMode) {
    return (
      <Shell>
        {confirming ? (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-8 rounded-3xl border border-border/60 bg-white p-8 text-center shadow-2xl" data-testid="payer-confirming">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-brand-green" />
            <h1 className="mt-4 font-heading text-xl font-bold">Confirming your payment…</h1>
            <p className="mt-1 text-sm text-muted-foreground">Hang tight, this only takes a few seconds.</p>
          </motion.div>
        ) : result ? (
          <ResultCard />
        ) : (
          <div className="mt-8 rounded-3xl border border-border/60 bg-white p-8 text-center shadow-2xl" data-testid="payer-return-error">
            <p className="text-muted-foreground">{error || "We couldn't confirm your payment."}</p>
          </div>
        )}
      </Shell>
    );
  }

  if (error && !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center">
        <TiplymeLogo />
        <p className="max-w-xs text-muted-foreground" data-testid="payer-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-brand-green" /></div>;
  }

  const { worker, workplace, preset_amounts } = data;

  return (
    <Shell>
      {!result ? (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="relative mt-8 rounded-3xl border border-border/60 bg-white p-6 shadow-2xl" data-testid="payer-card">
          <div className="flex items-center gap-4">
            <img src={worker.photo} alt={worker.name} className="h-20 w-20 rounded-2xl object-cover ring-4 ring-brand-green/10" data-testid="payer-worker-photo" />
            <div className="min-w-0">
              <p className="truncate font-heading text-xl font-bold" data-testid="payer-worker-name">{worker.name}</p>
              <p className="truncate text-sm text-muted-foreground">{worker.place_of_work || workplace.name}</p>
              <p className="mt-1 flex items-center gap-1 text-sm font-semibold text-brand-orange">
                <Star className="h-4 w-4 fill-brand-orange" /> {worker.rating_avg || "New"}
                {worker.rating_count > 0 && <span className="font-normal text-muted-foreground">({worker.rating_count})</span>}
              </p>
            </div>
          </div>

          {workplace.name && (
            <div className="mt-4 flex items-center gap-3 rounded-xl border border-border/60 bg-slate-50 p-3" data-testid="payer-workplace">
              {workplace.logo && <img src={workplace.logo} alt={workplace.name} className="h-9 w-9 rounded-lg object-cover" />}
              <div className="text-sm">
                <p className="overline-label text-muted-foreground">Workplace</p>
                <p className="font-semibold">{workplace.name}</p>
              </div>
            </div>
          )}

          <p className="mt-6 overline-label text-muted-foreground">Tap an amount to tip instantly</p>
          <div className="mt-2 grid grid-cols-2 gap-3">
            {preset_amounts.map((a) => (
              <button key={a} onClick={() => pay(a, a)} disabled={paying} data-testid={`preset-amount-${a}`}
                className="grid h-[68px] place-items-center rounded-2xl border border-border bg-brand-navy font-heading text-2xl font-bold text-white shadow-lg shadow-brand-navy/20 transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                {payingAmt === a ? <Loader2 className="h-6 w-6 animate-spin" /> : `R${a}`}
              </button>
            ))}
          </div>

          <div className="mt-4">
            <p className="overline-label text-muted-foreground">Or enter your own</p>
            <div className="mt-2 flex gap-2">
              <Input type="number" min="1" placeholder="Custom (R)" value={custom}
                onChange={(e) => setCustom(e.target.value)} data-testid="custom-amount-input"
                className="h-12 rounded-2xl text-center text-lg" />
              <Button onClick={() => pay(customAmount, "custom")} disabled={!customAmount || customAmount <= 0 || paying} data-testid="payer-pay-button"
                className="h-12 shrink-0 rounded-2xl bg-brand-cyan px-6 text-base font-bold text-white hover:bg-brand-cyan/90">
                {payingAmt === "custom" ? <Loader2 className="h-5 w-5 animate-spin" /> : "Pay"}
              </Button>
            </div>
          </div>

          {error && <p className="mt-4 rounded-xl bg-red-50 p-3 text-center text-sm font-semibold text-red-600" data-testid="payer-pay-error">{error}</p>}

          <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5 text-brand-cyan" /> One tap · Card, Capitec Pay, Apple Pay &amp; Google Pay</p>

          {paying && (
            <div className="absolute inset-0 z-10 grid place-items-center rounded-3xl bg-white/95 px-6 backdrop-blur-sm" data-testid="payer-redirecting">
              <div className="w-full text-center">
                <p className="overline-label text-muted-foreground">You're tipping</p>
                <div className="mx-auto mt-2 grid h-[92px] w-full max-w-[220px] place-items-center rounded-2xl bg-brand-navy font-heading text-5xl font-extrabold text-white shadow-lg shadow-brand-navy/20" data-testid="redirecting-amount">R{payingValue}</div>
                <div className="mt-5 flex items-center justify-center gap-2 text-sm font-semibold text-brand-navy">
                  <Loader2 className="h-5 w-5 animate-spin text-brand-cyan" /> Taking you to secure checkout…
                </div>
                <p className="mt-1 text-xs text-muted-foreground">If it doesn't open automatically, tap below. This screen updates once you've paid.</p>
                {redirectUrl && (
                  <a href={redirectUrl} data-testid="continue-checkout" className="mt-4 inline-block rounded-full bg-brand-cyan px-6 py-2.5 text-sm font-bold text-white hover:bg-brand-cyan/90">Open secure checkout</a>
                )}
              </div>
            </div>
          )}
        </motion.div>
      ) : (
        <ResultCard />
      )}
    </Shell>
  );
}
